import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { fetchSettings, updateSettings, clearError } from '../store/slices/settingsSlice';

function Settings() {
  const dispatch = useDispatch();
  const { settings, loading, error } = useSelector((state) => state.settings);
  const isInitialMount = useRef(true);
  const [formData, setFormData] = React.useState({
    defaultRedirectPercentage: 30,
    defaultDeviceTypes: ['mobile', 'tablet'],
    defaultTargetCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'IE', 'ZA', 'IN', 'SG', 'MY', 'PH', 'ID', 'TH', 'VN', 'JP', 'KR', 'CN', 'HK', 'TW', 'MO'],
    whitelistedDomains: [],
    allowedReferrers: [],
    notificationSettings: {
      slack: {
        enabled: false,
        webhookUrl: '',
      },
    },
  });
  const [newWhitelistedDomain, setNewWhitelistedDomain] = React.useState('');
  const [newAllowedReferrer, setNewAllowedReferrer] = React.useState('');
  const [domainError, setDomainError] = React.useState('');
  const [referrerError, setReferrerError] = React.useState('');

  const loadSettings = useCallback(async () => {
    if (!isInitialMount.current) return;
    
    try {
      await dispatch(fetchSettings()).unwrap();
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, [dispatch]);

  // Fetch settings only once when component mounts
  useEffect(() => {
    loadSettings();
    return () => {
      isInitialMount.current = false;
    };
  }, [loadSettings]);

  // Update form data when settings change
  useEffect(() => {
    if (settings) {
      setFormData(prev => ({
        ...prev,
        ...settings,
        whitelistedDomains: settings.whitelistedDomains || [],
        allowedReferrers: settings.allowedReferrers || [],
        notificationSettings: {
          ...prev.notificationSettings,
          ...(settings.notificationSettings || {}),
        },
      }));
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNotificationChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      notificationSettings: {
        ...prev.notificationSettings,
        [type]: {
          ...prev.notificationSettings[type],
          [field]: value,
        },
      },
    }));
  };

  const validateDomain = (domain) => {
    // Remove any protocol and trailing slashes
    const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    
    // Domain validation regex
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    
    if (!cleanDomain) {
      return 'Domain is required';
    }
    if (!domainRegex.test(cleanDomain)) {
      return 'Please enter a valid domain (e.g., example.com)';
    }
    if (formData.whitelistedDomains.includes(cleanDomain)) {
      return 'This domain is already in the list';
    }
    return '';
  };

  const validateReferrer = (referrer) => {
    if (!referrer) {
      return 'Referrer is required';
    }
    
    // Check if it's a valid URL or android-app scheme
    try {
      if (referrer.startsWith('android-app://')) {
        return '';
      }
      new URL(referrer);
      return '';
    } catch (e) {
      return 'Please enter a valid URL or android-app scheme';
    }
  };

  const handleAddWhitelistedDomain = async () => {
    const error = validateDomain(newWhitelistedDomain);
    setDomainError(error);
    
    if (!error) {
      const cleanDomain = newWhitelistedDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      const updatedFormData = {
        ...formData,
        whitelistedDomains: [...formData.whitelistedDomains, cleanDomain]
      };
      setFormData(updatedFormData);
      setNewWhitelistedDomain('');
      try {
        await dispatch(updateSettings(updatedFormData)).unwrap();
      } catch (error) {
        console.error('Failed to update settings:', error);
      }
    }
  };

  const handleRemoveWhitelistedDomain = async (domain) => {
    const updatedFormData = {
      ...formData,
      whitelistedDomains: formData.whitelistedDomains.filter(d => d !== domain)
    };
    setFormData(updatedFormData);
    try {
      await dispatch(updateSettings(updatedFormData)).unwrap();
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleAddAllowedReferrer = async () => {
    const error = validateReferrer(newAllowedReferrer);
    setReferrerError(error);
    
    if (!error) {
      const updatedFormData = {
        ...formData,
        allowedReferrers: [...formData.allowedReferrers, newAllowedReferrer]
      };
      setFormData(updatedFormData);
      setNewAllowedReferrer('');
      try {
        await dispatch(updateSettings(updatedFormData)).unwrap();
      } catch (error) {
        console.error('Failed to update settings:', error);
      }
    }
  };

  const handleRemoveAllowedReferrer = async (referrer) => {
    const updatedFormData = {
      ...formData,
      allowedReferrers: formData.allowedReferrers.filter(r => r !== referrer)
    };
    setFormData(updatedFormData);
    try {
      await dispatch(updateSettings(updatedFormData)).unwrap();
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateSettings(formData)).unwrap();
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 600,
          color: 'primary.main',
          mb: 4
        }}
      >
        Settings
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          onClose={() => dispatch(clearError())} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: 1
          }}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                borderRadius: 2,
                '&:hover': {
                  boxShadow: 3,
                }
              }}
            >
              <CardHeader 
                title="Default Settings" 
                titleTypographyProps={{
                  variant: 'h6',
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Default Redirect URL"
                      name="defaultRedirectUrl"
                      value={formData.defaultRedirectUrl}
                      onChange={handleChange}
                      variant="outlined"
                      disabled
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Default Redirect Percentage"
                      name="defaultRedirectPercentage"
                      type="number"
                      value={formData.defaultRedirectPercentage}
                      onChange={handleChange}
                      inputProps={{ min: 0, max: 100 }}
                      variant="outlined"
                      disabled
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                borderRadius: 2,
                '&:hover': {
                  boxShadow: 3,
                }
              }}
            >
              <CardHeader 
                title="Whitelisted Domains" 
                titleTypographyProps={{
                  variant: 'h6',
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Add Whitelisted Domain"
                        value={newWhitelistedDomain}
                        onChange={(e) => {
                          setNewWhitelistedDomain(e.target.value);
                          setDomainError('');
                        }}
                        error={!!domainError}
                        helperText={domainError}
                        variant="outlined"
                        size="small"
                        placeholder="example.com"
                      />
                      <IconButton 
                        color="primary" 
                        onClick={handleAddWhitelistedDomain}
                        disabled={!newWhitelistedDomain || !!domainError}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.whitelistedDomains.map((domain) => (
                        <Chip
                          key={domain}
                          label={domain}
                          onDelete={() => handleRemoveWhitelistedDomain(domain)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                borderRadius: 2,
                '&:hover': {
                  boxShadow: 3,
                }
              }}
            >
              <CardHeader 
                title="Allowed Referrers" 
                titleTypographyProps={{
                  variant: 'h6',
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Add Allowed Referrer"
                        value={newAllowedReferrer}
                        onChange={(e) => {
                          setNewAllowedReferrer(e.target.value);
                          setReferrerError('');
                        }}
                        error={!!referrerError}
                        helperText={referrerError}
                        variant="outlined"
                        size="small"
                        placeholder="https://example.com or android-app://com.example.app"
                      />
                      <IconButton 
                        color="primary" 
                        onClick={handleAddAllowedReferrer}
                        disabled={!newAllowedReferrer || !!referrerError}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.allowedReferrers.map((referrer) => (
                        <Chip
                          key={referrer}
                          label={referrer}
                          onDelete={() => handleRemoveAllowedReferrer(referrer)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                borderRadius: 2,
                '&:hover': {
                  boxShadow: 3,
                }
              }}
            >
              <CardHeader 
                title="Notification Settings" 
                titleTypographyProps={{
                  variant: 'h6',
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                      Slack Notifications
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.notificationSettings.slack.enabled}
                          onChange={(e) =>
                            handleNotificationChange('slack', 'enabled', e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Enable Slack Notifications"
                    />
                    {formData.notificationSettings.slack.enabled && (
                      <TextField
                        fullWidth
                        label="Slack Webhook URL"
                        value={formData.notificationSettings.slack.webhookUrl}
                        onChange={(e) =>
                          handleNotificationChange('slack', 'webhookUrl', e.target.value)
                        }
                        sx={{ 
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                        variant="outlined"
                      />
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                Save Settings
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Settings; 